import React, { JSX, useContext, useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";

// Public visitors get no account entry point (the login modal stays parked —
// restore `ModalContext` + LOGIN_MODAL when that changes). Logged-in team
// substore users get the account menu + cart icon on the right.
// import { ModalContext } from "context/modal";

import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { A11y } from "swiper/modules";
import { CartContext } from "context/cart";
import { UserContext } from "context/user";
import { colors } from "constants/colors";
import { assetsRoot, endpoints } from "config";

import { CircleArrowLeft, CircleArrowRight } from "components/PdpArt";
import { SmartButton } from "components/SmartCommerce";
import { getProductUpc } from "constants/commerce";
import { DrawerWave } from "./DrawerWave";
import st from "./header.module.scss";

interface PoppiNavLink {
  label: string;
  href: string;
  external?: boolean;
}

// poppi top navigation. Labels mirror drinkpoppi.com; hrefs are mapped to the
// routes that exist in this app so nothing 404s until the dedicated pages land.
const NAV_LINKS: PoppiNavLink[] = [
  { label: "shop", href: "/collections/drinks" },
  { label: "our story", href: "/pages/our-story" },
  { label: "why poppi?", href: "/pages/whypoppi" },
  { label: "find poppi", href: "/pages/find-poppi" },
];

const CartIcon = (): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 7h12l-1 13H7L6 7Zm3 0a3 3 0 1 1 6 0"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AccountIcon = (): JSX.Element => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M5 19c0-3.3 3.1-6 7-6s7 2.7 7 6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

// Scrolling announcement — mirrors the live marquee copy.
const MARQUEE_ITEMS = [
  "taste the unexpected",
  "our sweetest temptation yet",
  "are you tempted yet?",
  "one sip isn’t enough",
  "sweet, sour, slightly scandalous",
  "sour never tasted so sweet",
];

// live's shop-tab flyout lineup: 8 singles with their card theme vars
// (--color-primary/--color-secondary on each card article)
const SHOP_DROPDOWN: {
  slug: string;
  name: string;
  primary: string;
  secondary: string;
  img: string;
}[] = [
  {
    slug: "sour-apple",
    name: "Sour Apple",
    primary: "#bcff00",
    secondary: "#3e8549",
    img: `${assetsRoot}/poppi/sour-apple/can.png`,
  },
  {
    slug: "shirley-temple",
    name: "Shirley Temple",
    primary: "#f26b6b",
    secondary: "#8e0011",
    img: `${assetsRoot}/poppi/pdp/cans/shirley-temple.png`,
  },
  {
    slug: "orange-cream",
    name: "Orange Cream",
    primary: "#ffe6d1",
    secondary: "#e15505",
    img: `${assetsRoot}/poppi/pdp/cans/orange-cream.png`,
  },
  {
    slug: "strawberry-lemon",
    name: "Strawberry Lemon",
    primary: "#ec008c",
    secondary: "#fff200",
    img: `${assetsRoot}/poppi/pdp/cans/strawberry-lemon.png`,
  },
  {
    slug: "cream-soda",
    name: "Cream Soda",
    primary: "#0667be",
    secondary: "#e9e4c5",
    img: `${assetsRoot}/poppi/pdp/cans/cream-soda.png`,
  },
  {
    slug: "ginger-lime",
    name: "Ginger Lime",
    primary: "#e5fc0f",
    secondary: "#008cf1",
    img: `${assetsRoot}/poppi/pdp/cans/ginger-lime.png`,
  },
  {
    slug: "alpine-blast",
    name: "Alpine Blast",
    primary: "#45ce0d",
    secondary: "#0031a7",
    img: `${assetsRoot}/poppi/pdp/cans/alpine-blast.png`,
  },
  {
    slug: "doc-pop",
    name: "Doc Pop",
    primary: "#f66ff8",
    secondary: "#a40e56",
    img: `${assetsRoot}/poppi/pdp/cans/doc-pop.png`,
  },
];

export function NavHeader(): JSX.Element {
  // const { open } = useContext(ModalContext);
  const { currentUserId, logout } = useContext(UserContext);
  const { getSize } = useContext(CartContext);
  const cartSize = getSize() ?? 0;
  const router = useRouter();
  // PDPs hide the announcement marquee (the header re-themes to the product).
  const path = router?.pathname ?? "";
  const hasNoHeaderShadow =
    path === "/" || path === "/pages/vibes" || path === "/pages/summer";
  // PDPs + the full-screen Love Island microsite hide the announcement marquee.
  // The drinkpoppi page replicas match live: no marquee, no campaign link in
  // the nav; why-poppi and find-poppi additionally hide the account/cart icons.
  const isStory = path === "/pages/our-story";
  const isWhy =
    path === "/pages/whypoppi" ||
    path === "/pages/find-poppi" ||
    path === "/collections/drinks" ||
    path === "/collections/variety-packs" ||
    path === "/pages/poppi-university" ||
    path === "/pages/contact-us" ||
    path === "/pages/legal" ||
    path === "/pages/privacy-policy" ||
    path === "/pages/summer" ||
    path === "/pages/vibes" ||
    path === "/pages/brandnewdaysweeps" ||
    path === "/pages/pop-up-terms-conditions" ||
    path === "/pages/social-media-giveaway-terms-conditions" ||
    path === "/pages/poppi-desert-festival-kickback-2025" ||
    path === "/pages/sour-apple-terms-conditions" ||
    path === "/blogs/news" ||
    path.startsWith("/products");
  // Team substore PDPs drop the marquee too, but keep the account/cart icons.
  const isTeamPdp = path.startsWith("/team/products");
  const isPdp =
    path.startsWith("/products") ||
    isTeamPdp ||
    path === "/pages/love-island" ||
    isStory ||
    isWhy;
  const navLinks =
    isStory || isWhy
      ? NAV_LINKS.filter((l) => l.label !== "love island")
      : NAV_LINKS;

  const [menuOpen, setMenuOpen] = useState(false);
  // desktop shop-tab flyout (live: hover opens the product carousel dropdown)
  const [shopTabOpen, setShopTabOpen] = useState(false);
  const [sdSwiper, setSdSwiper] = useState<SwiperType | null>(null);
  const headerRef = React.useRef<HTMLElement>(null);
  const shopCloseTimer = React.useRef<number | null>(null);
  const shopClosePending = React.useRef(false);
  const headerBlurTimer = React.useRef<number | null>(null);
  const smartCommercePointerId = React.useRef<number | null>(null);
  const smartCommercePointerReleaseTimer = React.useRef<number | null>(null);
  const shopLinkRef = React.useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    const releaseSmartCommercePointer = (event: PointerEvent) => {
      if (event.pointerId !== smartCommercePointerId.current) return;

      if (smartCommercePointerReleaseTimer.current !== null) {
        window.clearTimeout(smartCommercePointerReleaseTimer.current);
      }
      const releasedInsideHeader =
        event.target instanceof Node &&
        Boolean(headerRef.current?.contains(event.target));
      // pointerup precedes click. Keep the dropdown interactive until the
      // vendor's retailer handler has received that click.
      smartCommercePointerReleaseTimer.current = window.setTimeout(() => {
        smartCommercePointerId.current = null;
        smartCommercePointerReleaseTimer.current = null;
        if (shopClosePending.current || !releasedInsideHeader) {
          shopClosePending.current = false;
          setShopTabOpen(false);
        }
      }, 0);
    };
    const cancelSmartCommercePointer = (event: PointerEvent) => {
      if (event.pointerId !== smartCommercePointerId.current) return;

      if (smartCommercePointerReleaseTimer.current !== null) {
        window.clearTimeout(smartCommercePointerReleaseTimer.current);
        smartCommercePointerReleaseTimer.current = null;
      }
      smartCommercePointerId.current = null;
      shopClosePending.current = false;
      setShopTabOpen(false);
    };

    window.addEventListener("pointerup", releaseSmartCommercePointer, true);
    window.addEventListener("pointercancel", cancelSmartCommercePointer, true);

    return () => {
      window.removeEventListener(
        "pointerup",
        releaseSmartCommercePointer,
        true
      );
      window.removeEventListener(
        "pointercancel",
        cancelSmartCommercePointer,
        true
      );
      if (smartCommercePointerReleaseTimer.current !== null) {
        window.clearTimeout(smartCommercePointerReleaseTimer.current);
      }
      if (shopCloseTimer.current !== null) {
        window.clearTimeout(shopCloseTimer.current);
      }
      if (headerBlurTimer.current !== null) {
        window.clearTimeout(headerBlurTimer.current);
      }
    };
  }, []);
  const openShopTab = () => {
    shopClosePending.current = false;
    if (shopCloseTimer.current !== null) {
      window.clearTimeout(shopCloseTimer.current);
      shopCloseTimer.current = null;
    }
    if (headerBlurTimer.current !== null) {
      window.clearTimeout(headerBlurTimer.current);
      headerBlurTimer.current = null;
    }
    setShopTabOpen(true);
  };
  const closeShopTab = () => {
    shopClosePending.current = true;
    if (shopCloseTimer.current !== null) {
      window.clearTimeout(shopCloseTimer.current);
    }
    shopCloseTimer.current = window.setTimeout(() => {
      shopCloseTimer.current = null;
      if (smartCommercePointerId.current === null) {
        shopClosePending.current = false;
        setShopTabOpen(false);
      }
    }, 400);
  };
  const closeShopTabImmediately = () => {
    shopClosePending.current = false;
    if (shopCloseTimer.current !== null) {
      window.clearTimeout(shopCloseTimer.current);
      shopCloseTimer.current = null;
    }
    if (headerBlurTimer.current !== null) {
      window.clearTimeout(headerBlurTimer.current);
      headerBlurTimer.current = null;
    }
    setShopTabOpen(false);
  };
  const handleHeaderBlur = () => {
    if (headerBlurTimer.current !== null) {
      window.clearTimeout(headerBlurTimer.current);
    }
    // SmartCommerce briefly reports a null relatedTarget while focus moves
    // between its injected buttons. Wait until focus settles before deciding
    // that the user actually left the header; otherwise pointer-events are
    // removed before the second button receives its click.
    headerBlurTimer.current = window.setTimeout(() => {
      headerBlurTimer.current = null;
      if (
        smartCommercePointerId.current === null &&
        !headerRef.current?.contains(document.activeElement)
      ) {
        closeShopTabImmediately();
      }
    }, 0);
  };
  const handleHeaderPointerDownCapture = (
    event: React.PointerEvent<HTMLElement>
  ) => {
    const target = event.target;
    if (
      !(target instanceof Element) ||
      !target.closest('[data-sc-type="SmartButton"]')
    ) {
      return;
    }

    smartCommercePointerId.current = event.pointerId;
    if (smartCommercePointerReleaseTimer.current !== null) {
      window.clearTimeout(smartCommercePointerReleaseTimer.current);
      smartCommercePointerReleaseTimer.current = null;
    }
    openShopTab();
  };
  const handleHeaderKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Escape" || !shopTabOpen) return;
    event.preventDefault();
    shopLinkRef.current?.focus();
    closeShopTabImmediately();
  };

  // Live drinkpoppi.com slides the whole header away (translateY(-88px))
  // while scrolling down past ~360px and brings it back on any upward
  // scroll. rAF-throttled so fast wheel events don't thrash state.
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    let last = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        if (y < last) setHidden(false);
        else if (y > last && y > 360) setHidden(true);
        last = y;
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const barHidden = hidden && !menuOpen;

  // the drawer takes over the viewport — freeze the page behind it
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  // Account menu for logged-in team substore users. Like the other pepdirect
  // storefronts, "my account" opens the shared portal dashboard and "log out"
  // ends the backend session (then returns to the team login).
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!accountOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!accountRef.current?.contains(event.target as Node)) {
        setAccountOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setAccountOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [accountOpen]);
  const accountUrl = `${endpoints.portalFrontend}/v3/dashboard/account/`;
  const onLogout = async () => {
    setAccountOpen(false);
    setMenuOpen(false);
    await logout();
    router?.push("/team/login");
  };

  const renderNavLink = (link: PoppiNavLink) =>
    link.external ? (
      <a
        key={link.label}
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        className={st.navLink}
        onClick={() => setMenuOpen(false)}
      >
        {link.label}
      </a>
    ) : (
      <Link
        key={link.label}
        href={link.href}
        className={st.navLink}
        onClick={() => setMenuOpen(false)}
      >
        {link.label}
      </Link>
    );

  return (
    <header
      ref={headerRef}
      className={st.header}
      onBlur={handleHeaderBlur}
      onKeyDown={handleHeaderKeyDown}
      onPointerDownCapture={handleHeaderPointerDownCapture}
    >
      <div
        className={`${st.bar} ${hasNoHeaderShadow ? st.barNoShadow : ""} ${
          barHidden ? st.barHidden : ""
        } ${menuOpen ? st.barMenuOpen : ""}`}
      >
        {/* Left: nav (desktop) + hamburger (mobile) */}
        <div className={st.left}>
          <button
            type="button"
            className={st.hamburger}
            aria-label={menuOpen ? "close menu" : "menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
          >
            {menuOpen ? (
              <svg
                className={st.closeGlyph}
                viewBox="0 0 32 32"
                aria-hidden="true"
              >
                <path
                  d="M11 11l10 10M21 11L11 21"
                  style={{ stroke: `var(--color-primary, ${colors.magenta})` }}
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              <>
                <span />
                <span />
                <span />
              </>
            )}
          </button>
          <nav className={st.navDesktop} aria-label="primary">
            {navLinks.map((link) =>
              link.label === "shop" ? (
                <span
                  key={link.label}
                  className={st.shopTab}
                  onMouseEnter={openShopTab}
                  onMouseLeave={closeShopTab}
                  onFocus={openShopTab}
                >
                  <Link
                    ref={shopLinkRef}
                    href={link.href}
                    className={st.navLink}
                    aria-expanded={shopTabOpen}
                    aria-controls="shop-products-menu"
                    onClick={closeShopTabImmediately}
                  >
                    {link.label}
                  </Link>
                </span>
              ) : (
                renderNavLink(link)
              )
            )}
          </nav>
        </div>

        {/* Center: poppi wordmark (masked so it re-colours with the theme) */}
        <Link href="/" className={st.logo} aria-label="poppi home">
          {/* live's inline wordmark (91×33), re-colored via currentColor */}
          <svg
            className={st.logoMark}
            viewBox="0 0 91 33"
            fill="none"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M19.1485 16.7871C19.1485 22.1368 15.3589 25.4152 11.2689 25.4152C10.0398 25.4152 9.07467 25.1806 8.27758 24.7123V27.9895C8.27758 29.6272 9.80704 30.1293 9.80704 31.1656V31.3004C9.80704 31.7344 9.47426 32.0015 8.97634 32.0015H1.1639C0.664728 32.0015 0.331523 31.7344 0.331523 31.3004V31.1656C0.331523 30.1293 1.69459 29.6272 1.69459 27.9895V15.3835C1.69459 13.8451 0 13.6448 0 12.6076V12.4073C0 11.9728 0.331523 11.6381 0.83112 11.3702L5.75061 8.92903C6.84854 8.3944 7.28007 8.92903 7.28007 9.63145V10.3001C8.07716 9.26418 9.47426 8.69531 11.2689 8.69531C14.9261 8.69531 19.1485 11.1698 19.1485 16.7871ZM12.5655 20.1644C12.5655 17.0212 11.9672 10.4674 9.70662 10.4674C8.77592 10.4674 8.27758 11.3376 8.27758 12.6744V18.8943C8.27758 21.9039 9.47426 23.6427 10.7041 23.6427C12.0008 23.6427 12.5655 22.472 12.5655 20.1644Z"
              fill="currentColor"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M20.4219 17.055C20.4219 12.04 24.8434 8.69531 29.697 8.69531C34.5841 8.69531 38.938 12.04 38.938 17.055C38.938 22.0705 34.5841 25.4152 29.697 25.4152C24.8434 25.4152 20.4219 22.0705 20.4219 17.055ZM32.0235 20.0976C32.0235 18.3255 31.0273 10.5017 28.5667 10.5017C27.27 10.5017 27.0713 11.7721 27.0713 14.0793C27.0713 16.2191 28.0675 23.6088 30.4957 23.6088C31.8243 23.6088 32.0235 22.2053 32.0235 20.0976Z"
              fill="currentColor"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M58.8634 16.7871C58.8634 22.1368 55.0738 25.4152 50.9854 25.4152C49.7547 25.4152 48.7895 25.1806 47.9928 24.7123V27.9895C47.9928 29.6272 49.5223 30.1293 49.5223 31.1656V31.3004C49.5223 31.7344 49.1895 32.0015 48.6912 32.0015H40.8775C40.3796 32.0015 40.0468 31.7344 40.0468 31.3004V31.1656C40.0468 30.1293 41.4107 29.6272 41.4107 27.9895V15.3835C41.4107 13.8451 39.7148 13.6448 39.7148 12.6076V12.4073C39.7148 11.9728 40.0468 11.6381 40.546 11.3702L45.4655 8.92903C46.5634 8.3944 46.9953 8.92903 46.9953 9.63145V10.3001C47.7937 9.26418 49.1895 8.69531 50.9854 8.69531C54.641 8.69531 58.8634 11.1698 58.8634 16.7871ZM52.2804 20.1644C52.2804 17.0212 51.682 10.4674 49.4219 10.4674C48.4908 10.4674 47.9928 11.3376 47.9928 12.6744V18.8943C47.9928 21.9039 49.1895 23.6427 50.4194 23.6427C51.7161 23.6427 52.2804 22.472 52.2804 20.1644Z"
              fill="currentColor"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M78.8517 16.7871C78.8517 22.1368 75.0616 25.4152 70.9732 25.4152C69.7425 25.4152 68.7778 25.1806 67.9807 24.7123V27.9895C67.9807 29.6272 69.5102 30.1293 69.5102 31.1656V31.3004C69.5102 31.7344 69.1774 32.0015 68.679 32.0015H60.8658C60.3679 32.0015 60.0346 31.7344 60.0346 31.3004V31.1656C60.0346 30.1293 61.399 29.6272 61.399 27.9895V15.3835C61.399 13.8451 59.7031 13.6448 59.7031 12.6076V12.4073C59.7031 11.9728 60.0346 11.6381 60.5338 11.3702L65.4537 8.92903C66.5517 8.3944 66.9832 8.92903 66.9832 9.63145V10.3001C67.782 9.26418 69.1774 8.69531 70.9732 8.69531C74.6288 8.69531 78.8517 11.1698 78.8517 16.7871ZM72.2682 20.1644C72.2682 17.0212 71.6703 10.4674 69.4097 10.4674C68.4786 10.4674 67.9807 11.3376 67.9807 12.6744V18.8943C67.9807 21.9039 69.1774 23.6427 70.4073 23.6427C71.7039 23.6427 72.2682 22.472 72.2682 20.1644Z"
              fill="currentColor"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M80.3281 24.3768V24.2432C80.3281 23.2056 81.6908 22.7048 81.6908 21.0658V15.6832C81.6908 14.0437 80.3281 13.5429 80.3281 12.6402V12.5058C80.3281 12.1055 80.6596 11.9035 81.1592 11.6698L86.8435 8.86139C87.841 8.36015 88.4717 8.96198 88.4717 9.63059V21.0658C88.4717 22.7048 90.0011 23.2056 90.0011 24.2432V24.3768C90.0011 24.8121 89.6692 25.0788 89.17 25.0788H81.1592C80.6596 25.0788 80.3281 24.8121 80.3281 24.3768ZM80.9265 3.67862C80.9265 1.60517 82.6211 0 85.148 0C87.6742 0 89.3692 1.60517 89.3692 3.67862C89.3692 5.75207 87.6742 7.38978 85.148 7.38978C82.6211 7.38978 80.9265 5.75207 80.9265 3.67862Z"
              fill="currentColor"
            />
          </svg>
        </Link>

        {/* live's shop-tab flyout: full-width panel under the bar (white,
            2px primary border, 12px radius, secondary drop-shadow) with the
            draggable 8-card singles carousel + shop-all */}
        <div
          id="shop-products-menu"
          className={`${st.shopDropdown} ${
            shopTabOpen ? st.shopDropdownOpen : ""
          }`}
          onMouseEnter={openShopTab}
          onMouseLeave={closeShopTab}
        >
          <div className={st.sdPanel}>
            <Swiper
              className={st.sdSwiper}
              modules={[A11y]}
              slidesPerView="auto"
              grabCursor
              onSwiper={setSdSwiper}
            >
              {SHOP_DROPDOWN.map((p) => (
                <SwiperSlide key={p.slug} className={st.sdSlide}>
                  <article
                    className={st.sdCard}
                    style={
                      {
                        "--sd-primary": p.primary,
                        "--sd-secondary": p.secondary,
                      } as React.CSSProperties
                    }
                  >
                    <div className={st.sdArch}>
                      <div className={st.sdCircle}>
                        <div className={st.sdCanClip}>
                          <Link
                            href={`/products/${p.slug}`}
                            className={st.sdCanLink}
                            tabIndex={shopTabOpen ? 0 : -1}
                            onClick={() => setShopTabOpen(false)}
                            aria-label={`poppi ${p.name}`}
                          >
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img src={p.img} alt="" className={st.sdCan} />
                          </Link>
                        </div>
                      </div>
                    </div>
                    <Link
                      href={`/products/${p.slug}`}
                      className={st.sdNameBar}
                      tabIndex={shopTabOpen ? 0 : -1}
                      onClick={() => setShopTabOpen(false)}
                    >
                      <h4>{p.name}</h4>
                    </Link>
                    {getProductUpc(p.slug) && (
                      <SmartButton
                        upc={getProductUpc(p.slug) as string}
                        productName={p.name}
                        className={st.sdAdd}
                        tabbable={shopTabOpen}
                        style={
                          {
                            "--color-primary": p.primary,
                            "--color-secondary": p.secondary,
                          } as React.CSSProperties
                        }
                      />
                    )}
                  </article>
                </SwiperSlide>
              ))}
            </Swiper>
            <div className={st.sdFooter}>
              <Link
                href="/collections/drinks"
                className={st.sdShopAll}
                tabIndex={shopTabOpen ? 0 : -1}
                onClick={() => setShopTabOpen(false)}
              >
                shop all
              </Link>
              <div className={st.sdArrows}>
                <button
                  type="button"
                  aria-label="Previous slide"
                  tabIndex={shopTabOpen ? 0 : -1}
                  onClick={() => sdSwiper?.slidePrev()}
                >
                  <CircleArrowLeft />
                </button>
                <button
                  type="button"
                  aria-label="Next slide"
                  tabIndex={shopTabOpen ? 0 : -1}
                  onClick={() => sdSwiper?.slideNext()}
                >
                  <CircleArrowRight />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right: account menu + cart, only for logged-in (team substore)
            users — the empty grid cell keeps the wordmark centered otherwise. */}
        <div className={st.right}>
          {currentUserId && (
            <>
              <div className={st.account} ref={accountRef}>
                <button
                  type="button"
                  className={st.iconBtn}
                  aria-label="account"
                  aria-haspopup="menu"
                  aria-expanded={accountOpen}
                  onClick={() => setAccountOpen((open) => !open)}
                >
                  <AccountIcon />
                </button>
                {accountOpen && (
                  <div className={st.accountMenu} role="menu" aria-label="account">
                    <a
                      role="menuitem"
                      className={st.accountItem}
                      href={accountUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setAccountOpen(false)}
                    >
                      my account
                    </a>
                    <button
                      type="button"
                      role="menuitem"
                      className={st.accountItem}
                      onClick={onLogout}
                    >
                      log out
                    </button>
                  </div>
                )}
              </div>
              <Link
                href="/cart"
                className={st.iconBtn}
                aria-label={`cart, ${cartSize} items`}
              >
                <CartIcon />
                {cartSize > 0 && (
                  <span className={st.cartCount}>{cartSize}</span>
                )}
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Scrolling announcement marquee (hidden on PDPs) */}
      <div className={`${st.marquee} ${isPdp ? st.marqueeHidden : ""}`}>
        {/* live wraps the whole marquee in a link to find-poppi */}
        <Link
          href="/pages/find-poppi"
          className={st.marqueeLink}
          aria-label="find poppi"
          style={
            {
              "--color-primary": "#CAFE26",
              "--color-secondary": "#027E34",
            } as React.CSSProperties
          }
        >
          <div className={st.marqueeTrack} aria-hidden="true">
            {[0, 1].map((dup) => (
              <div className={st.marqueeGroup} key={dup}>
                {MARQUEE_ITEMS.map((item, i) => (
                  <span className={st.marqueeItem} key={`${dup}-${i}`}>
                    <span className={st.marqueeText}>{item}</span>
                    <svg
                      className={st.sparkle}
                      viewBox="0 0 18 21"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M0 12.6006C0 12.6006 4.71429 13.2963 4.71429 21C4.71429 13.2963 9.42857 12.6006 9.42857 12.6006C9.42857 12.6006 4.71429 11.9041 4.71429 4.20001C4.71429 11.9037 0 12.6006 0 12.6006Z"
                        fill="currentColor"
                      />
                      <path
                        d="M7.71429 3.36062C7.71429 3.36062 9.42857 3.64005 9.42857 6.72C9.42857 3.64005 11.1429 3.36062 11.1429 3.36062C11.1429 3.36062 9.42857 3.08036 9.42857 0C9.42857 3.07995 7.71429 3.36062 7.71429 3.36062Z"
                        fill="currentColor"
                      />
                      <path
                        d="M13.7143 10.9204C13.7143 10.9204 15.8571 11.2695 15.8571 15.12C15.8571 11.2695 18 10.9204 18 10.9204C18 10.9204 15.8571 10.5713 15.8571 6.72C15.8571 10.5713 13.7143 10.9204 13.7143 10.9204Z"
                        fill="currentColor"
                      />
                    </svg>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </Link>
      </div>

      {/* Mobile drawer — full takeover below the bar, like live: the wave
          artwork (magenta band + yellow sweep + "shop pop" lettering) sits on
          top and the link list overlaps its bottom edge by 25px. */}
      {menuOpen && (
        <nav className={st.mobileDrawer} aria-label="primary mobile">
          <DrawerWave className={st.drawerWave} />
          <div className={st.drawerLinks}>{navLinks.map(renderNavLink)}</div>
          {currentUserId && (
            <div className={st.drawerAccount}>
              <a
                className={st.navLink}
                href={accountUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                my account
              </a>
              <button
                type="button"
                className={`${st.navLink} ${st.drawerButton}`}
                onClick={onLogout}
              >
                log out
              </button>
            </div>
          )}
        </nav>
      )}
    </header>
  );
}
